import * as SelectPrimitive from "@radix-ui/react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons";
import { forwardRef } from "react";
import cn from "mcn";

export const CustomSelect = SelectPrimitive.Root;
export const CustomSelectValue = SelectPrimitive.Value;

export const CustomSelectTrigger = forwardRef<HTMLButtonElement, React.ComponentProps<typeof SelectPrimitive.Trigger>>(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "h-9 px-2 py-1 pr-7 w-[120px] border border-outline rounded-lg bg-surface-1 text-foreground text-sm",
        "hover:bg-neutral-tonal",
        "focus:outline-none focus:ring-2 focus:ring-primary/20",
        "disabled:bg-card-muted disabled:border-card-muted disabled:text-outline disabled:cursor-not-allowed",
        "cursor-pointer relative",
        "data-[placeholder]:text-foreground-muted",
        "whitespace-nowrap overflow-hidden text-ellipsis text-left",
        className,
      )}
      {...props}
    >
      <span className="block overflow-hidden text-ellipsis whitespace-nowrap">{children}</span>
      <SelectPrimitive.Icon asChild>
        <FontAwesomeIcon
          icon={faCaretDown}
          className="text-neutral text-sm absolute right-2 top-1/2 -translate-y-1/2"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  ),
);
CustomSelectTrigger.displayName = "CustomSelectTrigger";

export const CustomSelectContent = forwardRef<HTMLDivElement, React.ComponentProps<typeof SelectPrimitive.Content>>(
  ({ className, children, position = "popper", sideOffset = 4, ...props }, ref) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 overflow-hidden rounded-lg border border-outline bg-card shadow-lg",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 max-h-[var(--radix-select-content-available-height)] w-[var(--radix-select-trigger-width)]",
          className,
        )}
        position={position}
        sideOffset={sideOffset}
        {...props}
      >
        <SelectPrimitive.Viewport className={cn("p-1", position === "popper" && "w-full")}>
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
);
CustomSelectContent.displayName = "CustomSelectContent";

export const CustomSelectItem = forwardRef<HTMLDivElement, React.ComponentProps<typeof SelectPrimitive.Item>>(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-md py-1.5 px-2 text-sm text-foreground outline-none",
        "hover:bg-neutral-tonal focus:bg-neutral-tonal",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "whitespace-nowrap overflow-hidden text-ellipsis",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  ),
);
CustomSelectItem.displayName = "CustomSelectItem";
